import { useParams, Link } from 'react-router-dom';
import { mangaWorks, categories } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';
import RatingStars from '@/components/feature/RatingStars';
import { useSEO } from '@/components/base/useSEO';

const articleImageUrls: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20romantic%20couple%20embracing%20on%20a%20city%20balcony%20at%20night%2C%20neon%20cityscape%20background%2C%20warm%20intimate%20lighting%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style%2C%20emotional%20atmosphere&width=900&height=500&seq=article-001-detail&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20conflicted%20woman%20in%20elegant%20evening%20setting%2C%20dramatic%20shadow%20and%20light%20contrast%2C%20luxurious%20interior%20background%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-002-detail&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20cherry%20blossom%20trees%20with%20high%20school%20students%20walking%20together%2C%20spring%20sunlight%2C%20pink%20petals%20falling%2C%20youthful%20atmosphere%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-003-detail&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20fantasy%20adventure%20party%20in%20magical%20forest%2C%20epic%20landscape%20with%20ancient%20ruins%2C%20sword%20and%20magic%20theme%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-004-detail&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20romantic%20office%20scene%20with%20a%20couple%20sharing%20coffee%2C%20modern%20corporate%20setting%2C%20warm%20evening%20lighting%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-005-detail&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20warrior%20in%20epic%20battle%20pose%20on%20a%20mountain%20cliff%2C%20red%20moon%20behind%2C%20dramatic%20action%20scene%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-006-detail&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20cozy%20apartment%20living%20room%20with%20a%20demon%20girl%20and%20a%20boy%20watching%20TV%2C%20warm%20home%20atmosphere%2C%20funny%20slice%20of%20life%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-007-detail&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20colorful%20fan%20art%20collection%20collage%20with%20various%20anime%20characters%2C%20vibrant%20colors%2C%20creative%20art%20studio%20theme%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-008-detail&orientation=landscape',
  9: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20grand%20magical%20academy%20campus%20with%20students%20in%20robes%2C%20floating%20candles%2C%20ancient%20castle%20architecture%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-009-detail&orientation=landscape',
  10: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20cozy%20late-night%20restaurant%20counter%20with%20customers%2C%20warm%20amber%20lighting%2C%20steaming%20food%2C%20intimate%20atmosphere%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-010-detail&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20teens%20studying%20in%20a%20summer%20classroom%2C%20bright%20sunlight%20through%20windows%2C%20youthful%20friendship%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-011-detail&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=Manga%20illustration%20of%20a%20Chinese%20empress%20in%20ancient%20palace%20garden%2C%20elegant%20hanfu%2C%20traditional%20architecture%2C%20golden%20sunset%20lighting%2C%20detailed%20character%20design%2C%20professional%20anime%20art%20style&width=900&height=500&seq=article-012-detail&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const work = mangaWorks.find(w => w.id === Number(id));

  const category = work ? categories.find(c => c.slug === work.category) : undefined;
  const relatedWorks = work ? mangaWorks
    .filter(w => w.category === work.category && w.id !== work.id)
    .slice(0, 4) : [];

  if (work) {
    const jsonLd = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: work.title,
      description: work.description,
      author: {
        '@type': 'Person',
        name: work.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '黄漫天堂',
        url: 'https://huangman.tt',
      },
      datePublished: work.updateDate,
      dateModified: work.updateDate,
      url: `https://huangman.tt/article/${work.id}`,
      image: articleImageUrls[work.id] || '',
      keywords: work.tags.join(', '),
      articleSection: category?.name || work.category,
      wordCount: work.content.length,
      inLanguage: 'zh-CN',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangman.tt/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://huangman.tt/category/${work.category}` },
          { '@type': 'ListItem', position: 3, name: work.title, item: `https://huangman.tt/article/${work.id}` },
        ],
      },
    });

    useSEO({
      title: `${work.title} - ${category?.name || ''} - 黄漫天堂`,
      description: work.description,
      keywords: `${work.title},${work.author},${work.tags.join(',')},${category?.name || ''},黄漫天堂`,
      canonical: `https://huangman.tt/article/${work.id}`,
      ogType: 'article',
      ogImage: articleImageUrls[work.id] || '',
      jsonLd,
    });
  }

  if (!work) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2 font-heading">作品未找到</h2>
          <p className="text-foreground-500 mb-4">该作品可能已被移除或链接无效</p>
          <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap inline-block">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16">
      <div className="bg-background-100/70 border-b border-background-200/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: category?.name || '分类', href: `/category/${work.category}` },
            { label: work.title },
          ]} />
        </div>
      </div>

      <article className="max-w-[1000px] mx-auto px-4 md:px-6 py-8">
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {work.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                {tag}
              </span>
            ))}
            <span className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
              work.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-600'
            }`}>
              {work.status}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4">
            {work.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {work.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line"></i>
              共{work.chapters}话
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {work.views >= 10000 ? `${(work.views / 10000).toFixed(1)}万` : work.views}次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              更新于 {work.updateDate}
            </span>
          </div>

          <div className="mt-4">
            <RatingStars rating={work.rating} size="lg" />
          </div>
        </header>

        <div className="rounded-xl overflow-hidden mb-8 bg-background-100">
          <img
            src={articleImageUrls[work.id] || articleImageUrls[1]}
            alt={work.title}
            className="w-full h-[300px] md:h-[450px] object-cover object-top"
          />
        </div>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">作品简介</h2>
          <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
            {work.description}
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">内容预览</h2>
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
            {work.content.split('\n\n').map((paragraph, i) => (
              <p key={i} className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">作品亮点</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {work.highlights.map((highlight, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-sm shrink-0">
                  <i className="ri-check-line"></i>
                </div>
                <span className="text-sm text-foreground-700">{highlight}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">作品信息</h2>
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-foreground-500 mb-1">作者</p>
                <p className="text-sm font-medium text-foreground-800">{work.author}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">分类</p>
                <p className="text-sm font-medium text-foreground-800">{category?.name || work.category}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">状态</p>
                <p className="text-sm font-medium text-foreground-800">{work.status}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">总话数</p>
                <p className="text-sm font-medium text-foreground-800">{work.chapters}话</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">评分</p>
                <p className="text-sm font-medium text-accent-600">{work.rating}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">阅读量</p>
                <p className="text-sm font-medium text-foreground-800">{work.views >= 10000 ? `${(work.views / 10000).toFixed(1)}万` : work.views}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">最近更新</p>
                <p className="text-sm font-medium text-foreground-800">{work.updateDate}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">标签</p>
                <p className="text-sm font-medium text-foreground-800">{work.tags.join('、')}</p>
              </div>
            </div>
          </div>
        </section>

        {relatedWorks.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-5">
              {category?.name ? `更多${category.name}作品` : '相关推荐'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {relatedWorks.map((rw) => (
                <Link
                  key={rw.id}
                  to={`/article/${rw.id}`}
                  className="block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="p-3 md:p-4">
                    <h4 className="font-semibold text-foreground-900 text-sm truncate font-heading">{rw.title}</h4>
                    <p className="text-xs text-foreground-500 mt-1">{rw.author}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      <span className="text-[10px] text-foreground-600">{rw.rating}</span>
                      <span className="text-[10px] text-foreground-400 ml-2">{rw.views >= 10000 ? `${(rw.views / 10000).toFixed(1)}万` : rw.views}阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}